/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { useTheme } from "@mui/material";
import { Box, Tooltip } from "@mui/material";
import { Link } from "@tanstack/react-router";

import { STATUS_COLOR_MAP } from "./constants";
import {
  MotionTileLogo,
  OuterWrapper,
  MotionTileName,
  MotionTileStatus,
  ShadowShape,
  TileShape,
} from "./ParentTile.styles";
import type { EventRollupInput } from "@/features/DragonTiles/status/rollup";
import type { DragonTile } from "@qoTypes/dragon-tiles";

import { isByocDragonTile } from "@/features/Connectors/byocTileDisplayName";
import { getTileAttention } from "@/utils/dragonTiles";
import {
  formatTileNameForDisplay,
  getVisibleTileNameLength,
} from "@/utils/tileDisplayUtils";

export type Variant = "large" | "medium" | "extraLarge";

interface ParentTileProps {
  tile: DragonTile;
  siteId: string;
  variant?: Variant;
  topAdornment?: React.ReactNode;
  mediaContent?: React.ReactNode;
  linkTo?: string;
  linkParams?: Record<string, string>;
  isSelected?: boolean;
  isComingSoon?: boolean;
  framesLoaded: boolean;
  events?: EventRollupInput[];
}

export function ParentTile({
  tile,
  siteId,
  variant = "large",
  topAdornment,
  mediaContent,
  linkTo,
  linkParams,
  isSelected,
  isComingSoon = false,
  framesLoaded,
  events,
}: ParentTileProps) {
  const theme = useTheme();
  const { status, message: needsAttentionMessage } = getTileAttention(
    tile,
    isComingSoon,
    { framesLoaded, events },
  );
  const isActiveOrRunning = status === "Active" || status === "Running";
  const bgColor = STATUS_COLOR_MAP[status];
  const textColor = isActiveOrRunning
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary;

  const isClickable = tile.is_clickable && !isComingSoon;
  const displayName = formatTileNameForDisplay(tile.name);
  const nameLength = getVisibleTileNameLength(tile.name);

  // Determine the route path based on tile type
  const getRoutePath = () => {
    if (tile.name === "Broadcast") {
      return "/dragon-tiles/$siteId/broadcast/list";
    }
    if (tile.slug === "connectors") {
      return "/dragon-tiles/$siteId/connectors";
    }
    if (isByocDragonTile(tile)) {
      return "/dragon-tiles/$siteId/byoc";
    }
    return "/dragon-tiles/$siteId/$parentTile";
  };

  const tileContent = (
    <>
      {topAdornment ? (
        <Box
          sx={{
            position: "absolute",
            zIndex: 5,
            left: "50%",
            top: "33%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "auto",
            display: "flex",
            width: "30px",
            height: "32px",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {topAdornment}
        </Box>
      ) : null}
      {mediaContent ? (
        <MotionTileLogo
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 1.35 }}
        >
          {mediaContent}
        </MotionTileLogo>
      ) : null}
      {!mediaContent ? (
        <MotionTileName
          variant={variant}
          color={textColor}
          nameLength={nameLength}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 1.35 }}
        >
          {displayName}
        </MotionTileName>
      ) : null}
      <Tooltip
        title={needsAttentionMessage ?? ""}
        disableHoverListener={!needsAttentionMessage}
      >
        <MotionTileStatus
          variant={variant}
          color={textColor}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 1.35 }}
        >
          {status}
        </MotionTileStatus>
      </Tooltip>
      <TileShape
        variant={variant}
        bgColor={bgColor}
        bgImg={isActiveOrRunning ? "/hex-smoothie.png" : undefined}
        hideTopGlyph={Boolean(topAdornment)}
      />
    </>
  );

  return (
    <OuterWrapper
      variant={variant}
      controlledSelected={isSelected}
      className="parent-tile"
    >
      {isClickable ? (
        <Link
          key={tile.id}
          to={(linkTo ?? getRoutePath()) as any}
          params={
            (linkParams ?? {
              siteId,
              parentTile: tile.id,
            }) as any
          }
          preload={false}
          activeProps={{
            className: "parent-tile_link parent-tile_link__active",
          }}
          inactiveProps={{ className: "parent-tile_link" }}
        >
          {tileContent}
        </Link>
      ) : (
        <Box
          className="parent-tile_link"
          sx={{ opacity: 0.6, pointerEvents: "none" }}
        >
          {tileContent}
        </Box>
      )}
      {isClickable && <ShadowShape variant={variant} />}
    </OuterWrapper>
  );
}
